<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Icon from '../icon/Icon.vue';

const props = defineProps({
  hasAssistants: {
    type: Boolean,
    default: false,
  },
  canSuggestReply: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['useSuggestion']);
const { t } = useI18n();
const route = useRoute();

const routePromptMap = {
  conversations: [
    {
      label: 'TEKOMI.COPILOT.PROMPTS.SUMMARIZE.LABEL',
      prompt: 'TEKOMI.COPILOT.PROMPTS.SUMMARIZE.CONTENT',
    },
    {
      label: 'TEKOMI.COPILOT.PROMPTS.SUGGEST.LABEL',
      prompt: 'TEKOMI.COPILOT.PROMPTS.SUGGEST.CONTENT',
      requestType: 'reply_suggestion',
    },
    {
      label: 'TEKOMI.COPILOT.PROMPTS.RATE.LABEL',
      prompt: 'TEKOMI.COPILOT.PROMPTS.RATE.CONTENT',
    },
  ],
  dashboard: [
    {
      label: 'TEKOMI.COPILOT.PROMPTS.HIGH_PRIORITY.LABEL',
      prompt: 'TEKOMI.COPILOT.PROMPTS.HIGH_PRIORITY.CONTENT',
    },
    {
      label: 'TEKOMI.COPILOT.PROMPTS.LIST_CONTACTS.LABEL',
      prompt: 'TEKOMI.COPILOT.PROMPTS.LIST_CONTACTS.CONTENT',
    },
  ],
};

const getCurrentRoute = () => {
  const path = route.path;
  if (path.includes('/conversations')) return 'conversations';
  if (path.includes('/dashboard')) return 'dashboard';
  return 'dashboard';
};

const promptOptions = computed(() => {
  const currentRoute = getCurrentRoute();
  const prompts = routePromptMap[currentRoute] || routePromptMap.conversations;

  return prompts.filter(
    prompt => prompt.requestType !== 'reply_suggestion' || props.canSuggestReply
  );
});

const handleSuggestion = opt => {
  const message = t(opt.prompt);
  emit(
    'useSuggestion',
    opt.requestType ? { message, requestType: opt.requestType } : message
  );
};
</script>

<template>
  <div class="flex-1 flex flex-col gap-6 px-2">
    <div class="flex flex-col space-y-4 py-4">
      <Icon icon="i-woot-tekomi" class="text-n-slate-9 text-4xl" />
      <div class="space-y-1">
        <h3 class="text-base font-medium text-n-slate-12 leading-8">
          {{ $t('TEKOMI.COPILOT.PANEL_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 leading-6">
          {{ $t('TEKOMI.COPILOT.KICK_OFF_MESSAGE') }}
        </p>
      </div>
    </div>
    <div v-if="!hasAssistants" class="w-full space-y-2">
      <p class="text-sm text-n-slate-11 leading-6">
        {{ $t('TEKOMI.ASSISTANTS.NO_ASSISTANTS_AVAILABLE') }}
      </p>
      <router-link
        :to="{
          name: 'tekomi_assistants_create_index',
          params: {
            accountId: route.params.accountId,
          },
        }"
        class="text-n-slate-11 underline hover:text-n-slate-12"
      >
        {{ $t('TEKOMI.ASSISTANTS.ADD_NEW') }}
      </router-link>
    </div>
    <div v-else class="w-full space-y-2">
      <span class="text-xs text-n-slate-10 block">
        {{ $t('TEKOMI.COPILOT.TRY_THESE_PROMPTS') }}
      </span>
      <div class="space-y-1">
        <button
          v-for="prompt in promptOptions"
          :key="prompt.label"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-slate-2 text-n-slate-11 flex items-center justify-between hover:bg-n-slate-3 transition-colors"
          @click="handleSuggestion(prompt)"
        >
          <span>{{ t(prompt.label) }}</span>
          <Icon icon="i-lucide-chevron-right" />
        </button>
      </div>
    </div>
  </div>
</template>
